import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useAuthContext } from "../../providers/AuthProvider";
import createUserFromClient from "../../clients/createUserFromClient";
import { addUserToCampaign } from "../../clients/addUserToCampaign";
import GlobalSnackbar, { Severity } from "../snackbarComponent/Snackbar";
import { userRoles } from "../../types/userRoles";

interface CreateAssignDialogProps {
  open: boolean;
  onClose: () => void;
  gigId: string;
  onAssignSuccess: (assignPayload: {
    userId: string;
    fullName: string;
    gigId: string;
    role: string;
    superiorId?: string;
  }) => void;
  companyName: string | undefined;
}

interface FormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  role: string | null;
  registeredFrom: string;
}
export const roles = {
  brand: { value: userRoles.brand, label: "Brand" },
  agency: { value: userRoles.agency, label: "Agency" },
  supervisor: { value: userRoles.supervisor, label: "Supervisor" },
  executor: { value: userRoles.executor, label: "Executor" },
};
export const CreateAssignDialog: React.FC<CreateAssignDialogProps> = ({
  open,
  onClose,
  gigId,
  onAssignSuccess,
  companyName,
}) => {
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    role: null,
    registeredFrom: "gogig",
  });

  const { authHeader, userDetails } = useAuthContext();
  const currentUserRole = userDetails?.role;
  const [showCampaignsDialog, setShowCampaignsDialog] = useState(false);
  const [campaignErrorMessage, setCampaignErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [userAlreadyExists, setUserAlreadyExists] = useState(false);
  const [existingUserId, setExistingUserId] = useState<string>("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarData, setSnackbarData] = useState<{
    message: string;
    severity: Severity;
  }>({ message: "", severity: undefined });
  const isValidPhoneNumber = (phone: string) => /^\d{10}$/.test(phone);

  const snackbar = (message: string, severity: Severity) => {
    setOpenSnackbar(true);
    setSnackbarData({ message, severity });
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleChange = (field: keyof FormData, value: string | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };
  function getRoleLabel(roleValue: string | null): string {
    if (!roleValue) return "no role";
    const roleEntry = Object.values(roles).find((r) => r.value === roleValue);
    return roleEntry?.label ?? roleValue;
  }

  const getSuperiorId = () => {
    if (!authHeader) return;
    const base64Url = authHeader.split(".")[1];
    const jsonPayload = decodeURIComponent(window.atob(base64Url));
    const payload = JSON.parse(jsonPayload);
    return payload.sub;
  };

  // Get available roles based on current user's role
  const getAvailableRoles = () => {
    switch (currentUserRole) {
      case userRoles.brand:
        return [roles.brand, roles.agency, roles.supervisor, roles.executor];
      case userRoles.agency:
        return [roles.agency, roles.supervisor, roles.executor];
      case userRoles.supervisor:
        return [roles.supervisor, roles.executor];
      default:
        return [roles.executor];
    }
  };

  const availableRoles = getAvailableRoles();

  const handleCreateUser = async () => {
    if (!authHeader) return;
    setLoading(true);

    try {
      const response = await createUserFromClient(authHeader, formData);
      const responseData = response.data as { message: string; userId: string };

      // Check if user creation was successful and we have a userId
      if (responseData.userId && formData.role) {
        const assignPayload = {
          userId: responseData.userId,
          gigId,
          role: formData.role,
          superiorId: getSuperiorId(),
        };
        await addUserToCampaign(authHeader, assignPayload);
        snackbar("User created and assigned successfully!", "success");
        setTimeout(() => {
          onAssignSuccess({
            fullName: formData.firstName + " " + formData.lastName,
            ...assignPayload,
          });
          handleClose();
        }, 1500);
      } else {
        snackbar(responseData.message, "error");
      }
    } catch (err: any) {
      if (err.errorCause === "userRoleAndAssignRoleDontMatch") {
        setShowCampaignsDialog(true);
        setCampaignErrorMessage(
          "This user is has a different role, you cannot change this role."
        );
      } else if (err.errorCause === "mappingAlreadyExists") {
        setShowCampaignsDialog(true);
        setCampaignErrorMessage(
          `${formData.firstName} ${formData.lastName} (${
            formData.phoneNumber
          }) is already assigned in this campaign as ${getRoleLabel(
            formData?.role
          )}`
        );
        snackbar("this user is already assigned  ", "warning");
      } else if (
        err.errorCause.message === "executorAlreadyExistsInAnotherGig"
      ) {
        setShowCampaignsDialog(true);
        setCampaignErrorMessage(
          `Executor is already assigned to ${
            err.errorCause.details.companyName === companyName
              ? err.errorCause.details.gigTitle
              : "another"
          } Gig.`
        );
      } else {
        snackbar("assigning failed ", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAssignExistingUser = async () => {
    if (!authHeader || !existingUserId || !formData.role) return;
    setLoading(true);

    try {
      const assignPayload = {
        userId: existingUserId,
        gigId,
        role: formData.role,
        superiorId: getSuperiorId(),
      };

      await addUserToCampaign(authHeader, assignPayload);
      snackbar("User assigned successfully!", "success");

      setTimeout(() => {
        onAssignSuccess({
          fullName: formData.firstName + " " + formData.lastName,
          ...assignPayload,
        });
        handleClose();
      }, 1500);
    } catch (err: any) {
      if (err.errorCause === "userRoleAndAssignRoleDontMatch") {
        setShowCampaignsDialog(true);
        setCampaignErrorMessage(
          "This user is has a different role, you cannot change this role."
        );
      } else if (err.errorCause === "mappingAlreadyExists") {
        setShowCampaignsDialog(true);
        setCampaignErrorMessage(
          `${formData.firstName} ${formData.lastName}(${
            formData.phoneNumber
          }) is already assigned in this campaign as ${getRoleLabel(
            formData?.role
          )}`
        );
      } else if (
        err.errorCause.message === "executorAlreadyExistsInAnotherGig"
      ) {
        setShowCampaignsDialog(true);
        setCampaignErrorMessage(
          `Executor is already assigned to ${
            err.errorCause.details.companyName === companyName
              ? err.errorCause.details.gigTitle
              : "another"
          } Gig.`
        );
      } else {
        snackbar("assigning failed ", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (userAlreadyExists) {
      handleAssignExistingUser();
    } else {
      handleCreateUser();
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        role: null,
        registeredFrom: "gogig",
      });
      setUserAlreadyExists(false);
      setExistingUserId("");
      setShowCampaignsDialog(false); // Add this line
      setCampaignErrorMessage("");
      onClose();
    }
  };

  const isValid =
    formData.firstName &&
    formData.lastName &&
    formData.phoneNumber &&
    isValidPhoneNumber(formData.phoneNumber) &&
    formData.role;

  const getButtonText = () => {
    if (loading) return "Processing...";
    if (userAlreadyExists) return "Assign";
    return "Assign User";
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Assign User</Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {userAlreadyExists && (
              <Alert severity="info">
                User already registered. You can proceed with assignment.
              </Alert>
            )}

            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
                disabled={loading}
                required
              />
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                disabled={loading}
                required
              />
            </Box>

            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <TextField
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => handleChange("phoneNumber", e.target.value)}
                disabled={loading}
                required
                error={
                  !!formData.phoneNumber &&
                  !isValidPhoneNumber(formData.phoneNumber)
                }
                helperText={
                  !!formData.phoneNumber &&
                  !isValidPhoneNumber(formData.phoneNumber)
                    ? "Phone number must be 10 digits"
                    : ""
                }
              />
              <FormControl fullWidth>
                <InputLabel>Role *</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => handleChange("role", e.target.value)}
                  disabled={loading}
                  label="Role *"
                  required
                >
                  {availableRoles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label ?? null}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !isValid}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {getButtonText()}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showCampaignsDialog}
        onClose={() => setShowCampaignsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">User Already Assigned</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            {campaignErrorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowCampaignsDialog(false)}
            color="primary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <GlobalSnackbar
        open={openSnackbar}
        message={snackbarData.message}
        onClose={handleCloseSnackbar}
        severity={snackbarData.severity}
        vertical={"top"}
        horizontal={"center"}
      />
    </>
  );
};
